import * as THREE from 'three';

/**
 * Reusable bright-cyan edge highlight — the solid-segment look used to mark a
 * "this is the wall/edge being aligned to" target. Shared by the LineTool's
 * edge-op alignment (`EdgeOpController`) and the Door/Window opening gizmos so
 * the visual language is identical and there's a single material/render-order
 * definition.
 *
 * Renders `depthTest: false` at a high render order so it sits on top of the
 * point cloud. The caller supplies the scene (so it can pick a tool-overlay
 * scene that renders after the EDL composite when needed).
 */

/** The light-cyan used for alignment edge highlights across the line tooling. */
export const EDGE_HIGHLIGHT_COLOR = 0x88ddff;

export interface EdgeSegment {
  start: THREE.Vector3;
  end: THREE.Vector3;
}

export class EdgeHighlight {
  private group: THREE.Group | null = null;
  private readonly material: THREE.LineBasicMaterial;
  private readonly connectorMaterial: THREE.LineDashedMaterial;
  private readonly renderOrder: number;

  constructor(
    private readonly scene: THREE.Scene,
    opts: { color?: number; opacity?: number; linewidth?: number; renderOrder?: number } = {},
  ) {
    const color = opts.color ?? EDGE_HIGHLIGHT_COLOR;
    const renderOrder = opts.renderOrder ?? 1201;
    this.material = new THREE.LineBasicMaterial({
      color,
      transparent: true,
      opacity: opts.opacity ?? 0.95,
      depthTest: false,
      linewidth: opts.linewidth ?? 3,
    });
    this.connectorMaterial = new THREE.LineDashedMaterial({
      color,
      dashSize: 0.15,
      gapSize: 0.08,
      transparent: true,
      opacity: 0.85,
      depthTest: false,
    });
    this.renderOrder = renderOrder;
  }

  /**
   * Show the given world-space segments, replacing any currently shown. When
   * `anchor` is supplied, also draw a dashed connector from it to each edge's
   * midpoint — the "this opening/handle aligns to that wall" relationship line.
   */
  setEdges(edges: ReadonlyArray<EdgeSegment>, anchor?: THREE.Vector3): void {
    this.clear();
    if (edges.length === 0) return;
    const group = new THREE.Group();
    group.renderOrder = this.renderOrder;
    for (const e of edges) {
      const geom = new THREE.BufferGeometry().setFromPoints([e.start, e.end]);
      const line = new THREE.Line(geom, this.material);
      line.renderOrder = this.renderOrder;
      group.add(line);

      if (anchor) {
        const mid = e.start.clone().add(e.end).multiplyScalar(0.5);
        const connGeom = new THREE.BufferGeometry().setFromPoints([anchor.clone(), mid]);
        const conn = new THREE.Line(connGeom, this.connectorMaterial);
        conn.computeLineDistances();
        conn.renderOrder = this.renderOrder;
        group.add(conn);
      }
    }
    this.scene.add(group);
    this.group = group;
  }

  /** Remove the highlight (disposes per-segment geometry; materials are reused). */
  clear(): void {
    if (!this.group) return;
    this.scene.remove(this.group);
    this.group.traverse((obj) => {
      if (obj instanceof THREE.Line) obj.geometry.dispose();
    });
    this.group = null;
  }

  /** Tear down: clear + dispose the shared materials. */
  dispose(): void {
    this.clear();
    this.material.dispose();
    this.connectorMaterial.dispose();
  }
}
